import { HttpSyncClient, SyncError, type OnlineSyncStats } from "@app/storage";
import { Button } from "@app/ui";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { diagnoseReminders, fireTestNotification } from "../../features/reminders/index.js";
import { DEV_FLAGS, getDevFlag, setDevFlag, type DevFlag } from "../../lib/dev-flags.js";
import { clearErrorLog, getErrorLog, subscribeErrorLog, type ErrorLogEntry } from "../../global-error-handlers.js";
import { detectPlatform, getServices } from "../../platform.js";
import { toast, useAuthStore, useSettingsStore, useSyncStore } from "../../stores/index.js";
import {
  TOUCH_PAN_AMP_MIN, TOUCH_PAN_AMP_MAX, TOUCH_FLING_MS_MIN, TOUCH_FLING_MS_MAX,
  TOUCH_FLIP_PX_MIN, TOUCH_FLIP_PX_MAX, TOUCH_MAX_STEP_MIN, TOUCH_MAX_STEP_MAX,
  TOUCH_PAN_AMP_DEFAULT, TOUCH_FLING_MS_DEFAULT, TOUCH_FLIP_PX_DEFAULT, TOUCH_MAX_STEP_DEFAULT,
} from "../../stores/settings.store.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";

/** "Developer" — only reachable when developer mode is on (guarded here so even
 *  a direct URL bounces back to About when it's off). */
export function DeveloperSettings() {
  const developerMode = useSettingsStore((s) => s.developerMode);
  if (!developerMode) return <Navigate to="../about" replace />;
  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title="App info">
        <AppInfoCard />
      </SettingsSection>
      <SettingsSection title="Database">
        <DatabaseInfoCard />
      </SettingsSection>
      <SettingsSection title="Error log">
        <ErrorLogCard />
      </SettingsSection>
      <SettingsSection title="Debug flags" description="Console logging escape hatches. No restart needed unless noted.">
        <DebugFlagsCard />
      </SettingsSection>
      <SettingsSection title="Notifications">
        <NotificationTestCard />
      </SettingsSection>
      <SettingsSection title="Touch pan">
        <TouchPanCard />
      </SettingsSection>
    </div>
  );
}

type TableName =
  | "tasks"
  | "recurrence_rules"
  | "recurrence_overrides"
  | "labels"
  | "topics"
  | "calendars"
  | "settings";

interface TableStat {
  table: TableName;
  rows: number;
  active: number | null;
  deleted: number | null;
  bytes: number;
  columns: ColumnStat[];
}

interface ColumnStat {
  name: string;
  type: string;
  notNull: boolean;
  pk: boolean;
}

interface DatabaseStats {
  generatedAt: string;
  adapter: string;
  logicalBytes: number;
  physicalBytes: number | null;
  pageSize: number | null;
  pageCount: number | null;
  freePages: number | null;
  sqliteObjects: { tables: number; indexes: number };
  tables: TableStat[];
  taskMakeup: [string, number][];
  syncMakeup: [string, number][];
  online: OnlineDatabaseState;
}

type OnlineDatabaseState =
  | { status: "available"; serverUrl: string; stats: OnlineSyncStats }
  | { status: "unavailable"; serverUrl: string | null; message: string }
  | { status: "error"; serverUrl: string | null; message: string };

function AppInfoCard() {
  let adapter = "unknown";
  try { adapter = getServices().db.constructor.name; } catch { /* not initialized */ }
  const rows: [string, string][] = [
    ["App version", __APP_VERSION__],
    ["Web version", __WEB_VERSION__],
    ["Build", new Date(__BUILD_TIME__).toLocaleString()],
    ["Runtime mode", import.meta.env.MODE],
    ["Platform", detectPlatform()],
    ["Storage adapter", adapter],
  ];
  return (
    <SettingsCard id="app-info" title="App info">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
    </SettingsCard>
  );
}

function DatabaseInfoCard() {
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await collectDatabaseStats());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  return (
    <SettingsCard
      id="database-info"
      title="Database"
      description="Local and online storage shape, row counts, tombstones, and approximate size."
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          {stats ? `Measured ${new Date(stats.generatedAt).toLocaleString()}` : "Not measured yet"}
        </div>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => void refresh()}>
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {error && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">{error}</p>}

      {stats && (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <InfoRow label="Adapter" value={stats.adapter} />
            <InfoRow label="Logical payload" value={formatBytes(stats.logicalBytes)} />
            <InfoRow label="SQLite file pages" value={stats.physicalBytes === null ? "unavailable" : formatBytes(stats.physicalBytes)} />
            <InfoRow label="Page size" value={stats.pageSize === null ? "unavailable" : formatBytes(stats.pageSize)} />
            <InfoRow label="Page count" value={stats.pageCount === null ? "unavailable" : String(stats.pageCount)} />
            <InfoRow label="Free pages" value={stats.freePages === null ? "unavailable" : String(stats.freePages)} />
            <InfoRow label="Schema objects" value={`${stats.sqliteObjects.tables} tables, ${stats.sqliteObjects.indexes} indexes`} />
          </dl>

          <div>
            <h4 className="mb-2 text-sm font-medium">Tables</h4>
            <div className="overflow-auto rounded-md border">
              <table className="w-full min-w-[560px] text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 font-medium">Table</th>
                    <th className="px-2 py-1 text-right font-medium">Rows</th>
                    <th className="px-2 py-1 text-right font-medium">Active</th>
                    <th className="px-2 py-1 text-right font-medium">Deleted</th>
                    <th className="px-2 py-1 text-right font-medium">Payload</th>
                    <th className="px-2 py-1 font-medium">Columns</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {stats.tables.map((t) => (
                    <tr key={t.table}>
                      <td className="px-2 py-1 font-mono">{t.table}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.rows}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.active ?? "-"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.deleted ?? "-"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatBytes(t.bytes)}</td>
                      <td className="px-2 py-1 text-muted-foreground">{formatColumns(t.columns)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <StatList title="Task makeup" rows={stats.taskMakeup} />
            <StatList title="Sync makeup" rows={stats.syncMakeup} />
          </div>

          <OnlineDatabasePanel online={stats.online} />
        </div>
      )}
    </SettingsCard>
  );
}

function OnlineDatabasePanel({ online }: { online: OnlineDatabaseState }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">Online sync database</h4>
      {online.status !== "available" ? (
        <div className="rounded-md border p-3 text-sm">
          <div className="text-muted-foreground">{online.serverUrl ?? "No sync server URL"}</div>
          <div className={online.status === "error" ? "mt-1 text-destructive" : "mt-1 text-muted-foreground"}>
            {online.message}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <InfoRow label="Server" value={online.serverUrl} />
            <InfoRow label="Measured" value={new Date(online.stats.generatedAt).toLocaleString()} />
            <InfoRow label="Cursor" value={online.stats.cursor} />
            <InfoRow label="Rows" value={`${online.stats.totalRows} total, ${online.stats.activeRows} active, ${online.stats.deletedRows} deleted`} />
            <InfoRow label="Compact JSONB payload" value={formatBytes(online.stats.dataBytes)} />
            <InfoRow label="Records relation" value={online.stats.recordsTableBytes === null ? "unavailable" : formatBytes(online.stats.recordsTableBytes)} />
            <InfoRow label="Records indexes" value={online.stats.recordsIndexBytes === null ? "unavailable" : formatBytes(online.stats.recordsIndexBytes)} />
          </dl>

          <div className="overflow-auto rounded-md border">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 font-medium">Kind</th>
                  <th className="px-2 py-1 text-right font-medium">Rows</th>
                  <th className="px-2 py-1 text-right font-medium">Active</th>
                  <th className="px-2 py-1 text-right font-medium">Deleted</th>
                  <th className="px-2 py-1 text-right font-medium">Payload</th>
                  <th className="px-2 py-1 text-right font-medium">Avg keys</th>
                  <th className="px-2 py-1 text-right font-medium">Max seq</th>
                  <th className="px-2 py-1 font-medium">Newest update</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {online.stats.kinds.map((k) => (
                  <tr key={k.kind}>
                    <td className="px-2 py-1 font-mono">{k.kind}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{k.rows}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{k.active}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{k.deleted}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatBytes(k.dataBytes)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{k.rows === 0 ? "0.0" : (k.keyCount / k.rows).toFixed(1)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{k.maxSeq}</td>
                    <td className="px-2 py-1 text-muted-foreground">{k.newestUpdatedAt ? new Date(k.newestUpdatedAt).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

function StatList({ title, rows }: { title: string; rows: [string, number][] }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">{title}</h4>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-md border p-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

async function collectDatabaseStats(): Promise<DatabaseStats> {
  const services = getServices();
  const [tasks, rules, overrides, labels, topics, calendars, settings] = await Promise.all([
    services.tasks.listAll(),
    services.recurrence.listAll(),
    services.recurrenceOverrides.listAll(),
    services.labels.listAll(),
    services.topics.listAll(),
    services.calendars.listAll(),
    services.settings.listAll(),
  ]);

  const rowsByTable: Record<TableName, Record<string, unknown>[]> = {
    tasks: tasks as unknown as Record<string, unknown>[],
    recurrence_rules: rules as unknown as Record<string, unknown>[],
    recurrence_overrides: overrides as unknown as Record<string, unknown>[],
    labels: labels as unknown as Record<string, unknown>[],
    topics: topics as unknown as Record<string, unknown>[],
    calendars: calendars as unknown as Record<string, unknown>[],
    settings: Object.entries(settings).map(([key, value]) => ({ key, value })),
  };

  const tables = await Promise.all(
    (Object.keys(rowsByTable) as TableName[]).map(async (table): Promise<TableStat> => {
      const rows = rowsByTable[table];
      const deletable = rows.some((r) => Object.prototype.hasOwnProperty.call(r, "deleted_at"));
      const deleted = deletable ? rows.filter((r) => r["deleted_at"] != null).length : null;
      return {
        table,
        rows: rows.length,
        active: deletable ? rows.length - (deleted ?? 0) : null,
        deleted,
        bytes: byteSize(rows),
        columns: await readColumns(services.db, table, rows),
      };
    }),
  );

  const [pageSize, pageCount, freePages, sqliteObjects] = await Promise.all([
    readPragmaNumber(services.db, "page_size"),
    readPragmaNumber(services.db, "page_count"),
    readPragmaNumber(services.db, "freelist_count"),
    readSqliteObjects(services.db),
  ]);

  return {
    generatedAt: new Date().toISOString(),
    adapter: services.db.constructor.name,
    logicalBytes: tables.reduce((sum, t) => sum + t.bytes, 0),
    physicalBytes: pageSize !== null && pageCount !== null ? pageSize * pageCount : null,
    pageSize,
    pageCount,
    freePages,
    sqliteObjects,
    tables,
    taskMakeup: [
      ["Total tasks/events", tasks.length],
      ["Active", tasks.filter((t) => !t.deleted_at).length],
      ["Deleted tombstones", tasks.filter((t) => t.deleted_at).length],
      ["Tasks", tasks.filter((t) => !t.is_event).length],
      ["Events", tasks.filter((t) => t.is_event).length],
      ["Open", tasks.filter((t) => t.status === "open").length],
      ["Done", tasks.filter((t) => t.status === "done").length],
      ["Snoozed", tasks.filter((t) => t.status === "snoozed").length],
      ["Cancelled", tasks.filter((t) => t.status === "cancelled").length],
      ["Scheduled", tasks.filter((t) => t.due_at !== null).length],
      ["Undated", tasks.filter((t) => t.due_at === null).length],
      ["All-day", tasks.filter((t) => t.all_day).length],
      ["Timed blocks", tasks.filter((t) => !t.all_day && t.duration_minutes > 0).length],
      ["Subtasks", tasks.filter((t) => t.subtask_parent_id !== null).length],
    ],
    syncMakeup: [
      ["Recurrence rules", rules.length],
      ["Recurrence overrides", overrides.length],
      ["Labels", labels.length],
      ["Topics", topics.length],
      ["Calendars", calendars.length],
      ["Settings keys", Object.keys(settings).length],
      ["Rows with server rev", tasks.filter((t) => t.rev > 0).length],
      ["Rows with external id", tasks.filter((t) => t.external_id || t.ical_uid || t.source).length],
    ],
    online: await collectOnlineDatabaseStats(),
  };
}

async function collectOnlineDatabaseStats(): Promise<OnlineDatabaseState> {
  const auth = useAuthStore.getState();
  const sync = useSyncStore.getState();
  if (!auth.loaded) await auth.load();
  if (!sync.loaded) await sync.load();

  const { accessToken } = useAuthStore.getState();
  const { url } = useSyncStore.getState();
  if (!url) return { status: "unavailable", serverUrl: null, message: "Set a sync server URL first." };
  if (!accessToken) return { status: "unavailable", serverUrl: url, message: "Sign in to sync before reading online database state." };

  const client = new HttpSyncClient(
    url,
    () => useAuthStore.getState().accessToken,
    () => useAuthStore.getState().refreshAccess(),
  );
  try {
    return { status: "available", serverUrl: url, stats: await client.stats() };
  } catch (e) {
    const message =
      e instanceof SyncError && e.status === 404
        ? "The configured sync server does not expose database stats yet."
        : e instanceof Error
          ? e.message
          : String(e);
    return { status: "error", serverUrl: url, message };
  }
}

async function readColumns(db: ReturnType<typeof getServices>["db"], table: TableName, rows: Record<string, unknown>[]): Promise<ColumnStat[]> {
  try {
    const cols = await db.select<Record<string, unknown>>(`PRAGMA table_info(${table})`);
    if (cols.length > 0) {
      return cols.map((c) => ({
        name: String(c["name"] ?? ""),
        type: String(c["type"] ?? ""),
        notNull: Number(c["notnull"] ?? 0) === 1,
        pk: Number(c["pk"] ?? 0) > 0,
      })).filter((c) => c.name);
    }
  } catch {
    // MemoryAdapter and some bridges do not expose PRAGMA metadata.
  }
  const names = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row)) names.add(key);
  return [...names].sort().map((name) => ({
    name,
    type: inferType(rows.map((r) => r[name])),
    notNull: false,
    pk: name === "id" || name === "key",
  }));
}

async function readPragmaNumber(db: ReturnType<typeof getServices>["db"], key: "page_size" | "page_count" | "freelist_count"): Promise<number | null> {
  try {
    const rows = await db.select<Record<string, unknown>>(`PRAGMA ${key}`);
    return asNumber(rows[0]?.[key]);
  } catch {
    return null;
  }
}

async function readSqliteObjects(db: ReturnType<typeof getServices>["db"]): Promise<{ tables: number; indexes: number }> {
  try {
    const rows = await db.select<{ type: string }>(
      `SELECT type FROM sqlite_master WHERE type IN ('table','index')`,
    );
    return {
      tables: rows.filter((r) => r.type === "table").length,
      indexes: rows.filter((r) => r.type === "index").length,
    };
  } catch {
    return { tables: 0, indexes: 0 };
  }
}

function asNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function byteSize(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function formatColumns(columns: ColumnStat[]): string {
  if (columns.length === 0) return "-";
  return columns.map((c) => `${c.name}${c.type ? `:${c.type.toLowerCase()}` : ""}${c.pk ? " pk" : ""}${c.notNull ? " not null" : ""}`).join(", ");
}

function inferType(values: unknown[]): string {
  const v = values.find((x) => x !== null && x !== undefined);
  if (v === undefined) return "unknown";
  if (Array.isArray(v)) return "array";
  return typeof v;
}

function ErrorLogCard() {
  const [entries, setEntries] = useState<ErrorLogEntry[]>(getErrorLog());
  useEffect(() => subscribeErrorLog(() => setEntries(getErrorLog())), []);
  return (
    <SettingsCard
      id="error-log"
      title="Error log"
      description="Recent uncaught errors and promise rejections captured since the app loaded."
      onReset={entries.length > 0 ? () => clearErrorLog() : undefined}
    >
      {entries.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No errors captured. 🎉</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {entries.map((e, i) => (
            <li key={i} className="py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{e.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{new Date(e.at).toLocaleTimeString()}</span>
              </div>
              <p className="text-sm text-destructive">{e.message}</p>
              {e.stack && (
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-2 font-mono text-xs leading-relaxed text-muted-foreground">
                  {e.stack}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </SettingsCard>
  );
}

function DebugFlagsCard() {
  const [, force] = useState(0);
  const toggle = (key: DevFlag, on: boolean) => { setDevFlag(key, on); force((n) => n + 1); };
  return (
    <SettingsCard id="debug-flags" title="Debug flags">
      <ul className="flex flex-col divide-y divide-border">
        {DEV_FLAGS.map((f) => {
          const on = getDevFlag(f.key);
          return (
            <li key={f.key} className="flex items-center justify-between gap-4 py-2">
              <div>
                <div className="text-sm font-medium">{f.label}{f.needsReload && <span className="ml-2 text-xs text-muted-foreground">(reload to apply)</span>}</div>
                <div className="text-xs text-muted-foreground">{f.desc}</div>
              </div>
              <Button variant={on ? "primary" : "outline"} size="sm" onClick={() => toggle(f.key, !on)}>
                {on ? "On" : "Off"}
              </Button>
            </li>
          );
        })}
      </ul>
    </SettingsCard>
  );
}

function NotificationTestCard() {
  const [busy, setBusy] = useState<"now" | "delayed" | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const diagnose = async () => {
    setDiagnosing(true);
    try {
      setReport(await diagnoseReminders());
    } catch (e) {
      setReport(`Diagnostics failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDiagnosing(false);
    }
  };

  const run = async (kind: "now" | "delayed") => {
    setBusy(kind);
    try {
      const { background } = await fireTestNotification(kind === "delayed" ? 60_000 : undefined);
      if (kind === "now") {
        toast.success("Test notification sent.");
      } else if (background) {
        toast.success("Scheduled for 1 minute from now — you can close the app; it should still fire.", 8000);
      } else {
        toast.info("Scheduled for 1 minute — keep the app open (background delivery needs the installed app).", 8000);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e), 8000);
    } finally {
      setBusy(null);
    }
  };

  return (
    <SettingsCard
      id="notification-test"
      title="Notification test"
      description={`Check that reminders actually reach you. "In 1 minute" schedules with the OS — close the app after tapping it to confirm notifications arrive while it's not running.`}
    >
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={busy !== null} onClick={() => void run("now")}>
          {busy === "now" ? "Sending…" : "Send test now"}
        </Button>
        <Button variant="outline" disabled={busy !== null} onClick={() => void run("delayed")}>
          {busy === "delayed" ? "Scheduling…" : "Test in 1 minute"}
        </Button>
        <Button variant="outline" disabled={diagnosing} onClick={() => void diagnose()}>
          {diagnosing ? "Checking…" : "Why aren't reminders firing?"}
        </Button>
      </div>
      {report !== null && (
        <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-input bg-muted/40 p-3 font-mono text-xs leading-relaxed">
          {report}
        </pre>
      )}
    </SettingsCard>
  );
}

function TouchPanCard() {
  const touchPanAmp = useSettingsStore((s) => s.touchPanAmp);
  const setTouchPanAmp = useSettingsStore((s) => s.setTouchPanAmp);
  const touchPanFlingMs = useSettingsStore((s) => s.touchPanFlingMs);
  const setTouchPanFlingMs = useSettingsStore((s) => s.setTouchPanFlingMs);
  const touchPanFlipPx = useSettingsStore((s) => s.touchPanFlipPx);
  const setTouchPanFlipPx = useSettingsStore((s) => s.setTouchPanFlipPx);
  const touchPanMaxStep = useSettingsStore((s) => s.touchPanMaxStep);
  const setTouchPanMaxStep = useSettingsStore((s) => s.setTouchPanMaxStep);

  const reset = () => {
    void setTouchPanAmp(TOUCH_PAN_AMP_DEFAULT);
    void setTouchPanFlingMs(TOUCH_FLING_MS_DEFAULT);
    void setTouchPanFlipPx(TOUCH_FLIP_PX_DEFAULT);
    void setTouchPanMaxStep(TOUCH_MAX_STEP_DEFAULT);
  };

  return (
    <SettingsCard
      id="touch-pan"
      title="Touch pan"
      description={`Tuning for dragging the week/day calendar left–right with a finger. Higher amplification and fling, and a lower flip threshold, make panning feel less "sticky".`}
      onReset={reset}
    >
      <div className="flex flex-col gap-4">
        <DebugSlider label="Amplification" value={touchPanAmp} min={TOUCH_PAN_AMP_MIN} max={TOUCH_PAN_AMP_MAX} step={0.01}
          onChange={setTouchPanAmp} hint="Track moves this much more than the finger (1 = 1:1)." />
        <DebugSlider label="Fling projection (ms)" value={touchPanFlingMs} min={TOUCH_FLING_MS_MIN} max={TOUCH_FLING_MS_MAX} step={5}
          onChange={setTouchPanFlingMs} hint="How far a flick's release velocity carries into extra days." />
        <DebugSlider label="Page-flip threshold (px)" value={touchPanFlipPx} min={TOUCH_FLIP_PX_MIN} max={TOUCH_FLIP_PX_MAX} step={1}
          onChange={setTouchPanFlipPx} hint="Finger travel needed to page one day on release (lower = easier)." />
        <DebugSlider label="Max days per swipe" value={touchPanMaxStep} min={TOUCH_MAX_STEP_MIN} max={TOUCH_MAX_STEP_MAX} step={1}
          onChange={setTouchPanMaxStep} hint="Cap on how many days a single swipe can advance." />
      </div>
    </SettingsCard>
  );
}

function DebugSlider({ label, value, min, max, step, onChange, hint }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => Promise<void> | void; hint?: string;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => void onChange(Number(e.target.value))} className="w-full accent-primary" aria-label={label} />
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
